import { Filter } from '@aionzdev/design-system';
import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Endereco } from '../../../core/models/endereco';
import { SelectorListResponse } from '../shared/selector-list/interfaces/selector-list-response.interface';
import { ApproveQuotationMap } from '../interfaces/approve-quotation-map';
import { AddSupplierToProductDto } from '../interfaces/add-supplier-to-product.dto';
import { BuyerItem } from '../interfaces/buyer-item.interface';
import { CustomerTradingFocItem } from '../interfaces/customer-trading-foc-dialog-data';
import { DuplicatedProductResponseDto } from '../interfaces/duplicated-product-response.dto';
import { FindAllPurchasesRequestDto } from '../interfaces/find-purchase-request.dto';
import { MergePurchaseRequestItemsDto } from '../interfaces/merge-purchase-request-items.dto';
import { ProductIntegrationDialogData } from '../interfaces/product-integration-dialog-data';
import { PurchaseRequestDocumentUploadItemResult } from '../interfaces/purchase-request-document-upload-item-result';
import { PurchaseRequestAttachmentGroup } from '../interfaces/purchase-request-attachment-group';
import { PurchaseRequestResponseDto } from '../interfaces/purchase-response.dto';
import { PurchaseSupplier, PurchaseSupplierCreate } from '../interfaces/purchase-supplier';
import { QuotationMapData } from '../interfaces/quotation-map-data';
import { QuotationMapDraft } from '../interfaces/quotation-map-draft';
import { SendPurchaseRequestForQuoteItem } from '../interfaces/send-purchase-request-for-quote-dialog-data';
import { PurchaseRequestStatus, UiCapability } from '../enums';
import { SupplierInfo } from '../interfaces/supplier-info';
import { UpdatePurchaseRequestStatusDto } from '../interfaces/update-purchase-request-status.dto';
import { UpdatePurchaseOrderItemDto } from '../interfaces/update-purchase-order-status.dto';
import { UpdatePurchaseItemInfoDto } from '../interfaces/update-purchase-item-info.dto';
import { UploadOfferDocumentsDto } from '../interfaces/upload-offer-documents.dto';
import { RealtimeConfig } from '../interfaces/realtime-config';
import { PurchasingEvent } from '../interfaces/purchasing-event';
import { TimePerStageMetrics } from '../interfaces/time-per-stage-metrics';
import { PurchaseRequestCommentDto } from '../interfaces/purchase-request-comment.dto';
import { PurchaseRequestBranch } from '../interfaces/purchase-request-branch';
import { PurchaseRequestAuditDto, PurchaseRequestAuditRequestDto } from '../interfaces/purchase-request-audit.dto';
import { UpdateSuggestedSupplierStatusDto } from '../interfaces/update-suggested-supplier-status.dto';
import { TriggerSupplierSuggestionSearchResult } from '../interfaces/trigger-supplier-suggestion-search-result';
import { TradingMatchSuggestion } from '../interfaces/trading-match-suggestion';
import { UpdateTradingMatchSuggestionStatusDto } from '../interfaces/update-trading-match-suggestion-status.dto';



@Injectable({
  providedIn: 'root'
})
export abstract class AbstractPurchasingService {
  abstract setCache(id: string, data: PurchaseRequestResponseDto): void;
  abstract getCache(id: string): PurchaseRequestResponseDto | undefined;

  abstract capabilities$: Observable<UiCapability[]>;

  abstract findAllPurchases(
    data?: FindAllPurchasesRequestDto
  ): Observable<HttpResponse<PurchaseRequestResponseDto[]>>;

  abstract findPurchaseById(
    purchaseId: string
  ): Observable<HttpResponse<PurchaseRequestResponseDto>>;

  abstract getQuotationMapBoardData(purchaseId: string): Observable<HttpResponse<QuotationMapData>>;

  abstract approveQuotationMap(
    data: ApproveQuotationMap,
    config?: { hideGlobalError?: boolean }
  ): Observable<HttpResponse<void>>;

  /**
   * Rascunho do mapa de cotação (seleções + preços/fretes negociados ainda não
   * aprovados). O app persiste no `/form-temp` do ebarn-node, chaveado por
   * usuário logado + `mapa-cotacao-<purchaseId>`. `getQuotationMapDraft`
   * resolve `null` quando não há rascunho — o board abre limpo.
   */
  abstract getQuotationMapDraft(purchaseId: string): Observable<QuotationMapDraft | null>;
  abstract saveQuotationMapDraft(purchaseId: string, draft: QuotationMapDraft): Observable<void>;
  abstract deleteQuotationMapDraft(purchaseId: string): Observable<void>;

  /*abstract updatePurchaseRequest(
    data: UpdatePurchaseRequestDto
  ): Observable<HttpResponse<PurchaseRequestResponseDto>>;*/

  abstract updatePurchaseRequestStatus(data: UpdatePurchaseRequestStatusDto): Observable<void>;

  abstract updatePurchaseOrdersStatus(purchaseId: string, items: UpdatePurchaseOrderItemDto[]): Observable<void>;

  abstract updatePurchaseItemInfo(orderId: string, data: UpdatePurchaseItemInfoDto): Observable<void>;

  abstract startPurchaseRequestQuoting(purchaseId: string): Observable<void>;
  abstract acceptPurchaseRequestOffers(purchaseId: string): Observable<void>;

  abstract downloadPurchaseFile(purchaseId: string): Observable<Blob>;

  abstract downloadSupplierQuoteFile(purchaseId: string, supplierId: string): Observable<Blob>;

  abstract findAllProductsForQuote(
    purchaseId: string,
    status?: PurchaseRequestStatus
  ): Observable<HttpResponse<SendPurchaseRequestForQuoteItem[]>>;

  abstract findPurchaseRequestAttachments(
    purchaseId: string,
    status?: PurchaseRequestStatus
  ): Observable<HttpResponse<PurchaseRequestAttachmentGroup[]>>;

  abstract downloadPurchaseRequestAttachment(
    purchaseId: string,
    attachmentId: string
  ): Observable<Blob>;

  abstract getCustomerTradingFocs(purchaseId: string): Observable<CustomerTradingFocItem[]>;

  abstract createSupplier(
    supplier: PurchaseSupplierCreate
  ): Observable<HttpResponse<PurchaseSupplier>>;

  /** `hideGlobalError`: quem chama trata o erro (ex.: 409 "já estava no item" nos envios em lote). */
  abstract createSupplierForProduct(
    supplier: PurchaseSupplierCreate,
    config?: { hideGlobalError?: boolean }
  ): Observable<HttpResponse<PurchaseSupplier>>;

  abstract addSupplierToProduct(
    data: AddSupplierToProductDto
  ): Observable<HttpResponse<void>>;

  abstract deleteSupplier(supplierId: string, purchaseId?: string): Observable<HttpResponse<void>>;

  abstract deleteSupplierProducts(supplierId: string, productId: string): Observable<HttpResponse<void>>;

  abstract checkForDuplicatedProducts(
    purchaseId: string
  ): Observable<HttpResponse<DuplicatedProductResponseDto[]>>;

  abstract mergePurchaseRequestItems(data: MergePurchaseRequestItemsDto): Observable<boolean>;

  abstract getSupplierInfo(purchaseId: string): Observable<SupplierInfo[]>;

  abstract resendQuotationRequest(supplierId: string, purchaseId: string): Observable<boolean>;

  abstract findUncompletedPurchases(
    purchaseId: string
  ): Observable<HttpResponse<ProductIntegrationDialogData>>;

  /**
   * Cotações aguardando aprovação de cada item da solicitação (id do item → ids das cotações
   * vencedoras do mapa), para a tela de aprovação. Default: nenhuma.
   */
  findApprovalWinners(purchaseId: string): Observable<Record<string, string[]>> {
    return of({});
  }

  abstract getAverageSlaLabels(filters?: Filter[]): Observable<TimePerStageMetrics>;

  abstract findBuyersByGroupAndUserName(dto: {
    search: string;
    currentPage: number;
    take: number;
  }): Observable<SelectorListResponse<BuyerItem>>;

  abstract updateBuyer(purchaseId: string, buyerId: string): Observable<void>;

  abstract cancelPurchaseRequest(purchaseId: string, reason: string): Observable<void>;

  abstract uploadOfferDocuments(
    data: UploadOfferDocumentsDto
  ): Observable<PurchaseRequestDocumentUploadItemResult[]>;

  abstract getRealtimeConfig(): RealtimeConfig;

  abstract getPurchasingEvent(event: any): PurchasingEvent;

  /** `hideGlobalError`: não mostra o dialog global de erro (ex.: pré-carregamento em segundo plano). */
  abstract getPurchaseRequestComments(
    purchaseId: string,
    config?: { hideGlobalError?: boolean }
  ): Observable<HttpResponse<PurchaseRequestCommentDto[]>>;

  abstract sendPurchaseRequestCommentFile(
    purchaseId: string,
    file: File
  ): Observable<HttpResponse<string>>;

  abstract createPurchaseRequestComment(
    purchaseId: string,
    text: string,
    fileUrl?: string
  ): Observable<HttpResponse<PurchaseRequestCommentDto>>;

  abstract getPurchaseRequestAudits(data: PurchaseRequestAuditRequestDto): Observable<HttpResponse<PurchaseRequestAuditDto[]>>;

  abstract hasCapability(capability: UiCapability): boolean;

  abstract getAddress(): Observable<PurchaseRequestBranch[]>;

  /**
   * Endereços da empresa com cidade e estado, para completar o endereço dos locais de entrega
   * (as rotas de itens só devolvem os ids da cidade e do estado). Default: lista vazia, e os
   * locais aparecem só com rua e CEP.
   */
  getBranchAddresses(): Observable<Endereco[]> {
    return of([]);
  }

  /**
   * Status da integração com o ERP para a empresa (tenant) atual. Default:
   * true (mostra a coluna "Pendente de Integração", comportamento atual).
   * Apps que sabem se a empresa tem ERP configurado sobrescrevem.
   */
  getCompanyIntegrationStatus(): Observable<{ integracaoErpAtiva: boolean }> {
    return of({ integracaoErpAtiva: true });
  }

  abstract updateSuggestedSupplierStatus(
    data: UpdateSuggestedSupplierStatusDto
  ): Observable<HttpResponse<void>>;

  abstract triggerSupplierSuggestionSearch(
    purchaseId: string
  ): Observable<HttpResponse<TriggerSupplierSuggestionSearchResult>>;

  abstract getTradingMatchSuggestions(
    purchaseId: string
  ): Observable<HttpResponse<TradingMatchSuggestion[]>>;

  abstract updateTradingMatchSuggestionStatus(
    data: UpdateTradingMatchSuggestionStatusDto
  ): Observable<HttpResponse<void>>;
}
